import React, { useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Label, Input } from "reactstrap";
import { createNewUser } from "./Axios";
import FieldWrapper from "../commons/FieldWrapper";
import { format } from "rut.js";
import Swal from "sweetalert2";

const isValidRut = (rutCompleto) => {
  rutCompleto = rutCompleto.replace(/^0+|[^0-9kK]+/g, '').toUpperCase();
  if (rutCompleto.length < 2) return false;

  const cuerpo = rutCompleto.slice(0, -1);
  const dv = rutCompleto.slice(-1);

  let suma = 0;
  let multiplo = 2;

  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo.charAt(i)) * multiplo;
    multiplo = multiplo < 7 ? multiplo + 1 : 2;
  }

  const dvEsperado = 11 - (suma % 11);
  const dvStr = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : dvEsperado.toString();

  return dvStr === dv;
};

const cleanRutKeepDash = (rut) => {
  return rut.replace(/\./g, '');
};

const CreateUserModal = ({ buttonSize, showNotification, reloadQuery }) => {
  const [modal, setModal] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [rut, setRut] = useState("");
  const [phone, setPhone] = useState("");
  const [cvFile, setCvFile] = useState(null);
  const [errors, setErrors] = useState({});

  const toggle = () => setModal(!modal);

  const resetForm = () => {
    setFirstName("");
    setMiddleName("");
    setLastName("");
    setEmail("");
    setRut("");
    setPhone("");
    setCvFile(null);
    setErrors({});
  };

  const handleRutChange = (e) => {
    setRut(format(e.target.value));
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!firstName) newErrors.firstName = "El nombre es obligatorio.";
    if (!lastName) newErrors.lastName = "El apellido es obligatorio.";
    if (!email) newErrors.email = "El email es obligatorio.";
    if (!rut) newErrors.rut = "El RUT es obligatorio.";
    if (!phone) newErrors.phone = "El teléfono es obligatorio.";
    if (rut && !isValidRut(rut)) newErrors.rut = "RUT inválido (dígito verificador incorrecto)";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const password = `Rut${cleanRutKeepDash(rut)}`;

    Swal.fire({
      title: "¿Confirmar creación de usuario?",
      html: `
        <div style="text-align: left;">
          <b>Nombre:</b> ${firstName} ${middleName} ${lastName}<br/>
          <b>Email:</b> ${email}<br/>
          <b>RUT:</b> ${format(rut)}<br/>
          <b>Teléfono:</b> ${phone}<br/>
          <b>Contraseña:</b> ${password}<br/>
          ${cvFile ? `<b>CV:</b> ${cvFile.name}` : ""}
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, crear",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        // 👉 Enviar como FormData (multipart)
        const formData = new FormData();
        formData.append("user[first_name]", firstName);
        formData.append("user[middle_name]", middleName || "");
        formData.append("user[last_name]", lastName);
        formData.append("user[email]", email);
        formData.append("user[rut]", format(rut));
        formData.append("user[cellphone]", phone);
        if (cvFile) formData.append("cv", cvFile);

        createNewUser(formData, response => {
          if (response && response.status === 200) {
            toggle();
            resetForm();

            const cvUploaded = response?.data?.cv_uploaded ? " (CV cargado ✅)" : "";
            Swal.fire({
              title: "Usuario creado exitosamente",
              html: `
                <div style="text-align: left;">
                  <b>Email:</b> ${email}<br/>
                  <b>Contraseña:</b> ${password}${cvUploaded}
                </div>
              `,
              icon: "success",
            }).then(() => {
              reloadQuery();
            });

          } else {
            const errorMessage = response?.data?.errors?.join(", ") || "Error desconocido al crear usuario.";
            showNotification("Error", errorMessage, "danger");
          }
        });
      }
    });
  };

  return (
    <>
      <Button color="success" size={buttonSize} outline onClick={toggle}>
        Crear Usuario
      </Button>
      <Modal isOpen={modal} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>Crear Nuevo Usuario</ModalHeader>
        <ModalBody>
          <FieldWrapper errors={errors.firstName ? [errors.firstName] : []}>
            <Label>Primer nombre</Label>
            <Input value={firstName} onChange={e => setFirstName(e.target.value)} invalid={!!errors.firstName} />
          </FieldWrapper>

          <FieldWrapper>
            <Label>Segundo nombre</Label>
            <Input value={middleName} onChange={e => setMiddleName(e.target.value)} />
          </FieldWrapper>

          <FieldWrapper errors={errors.lastName ? [errors.lastName] : []}>
            <Label>Apellido</Label>
            <Input value={lastName} onChange={e => setLastName(e.target.value)} invalid={!!errors.lastName} />
          </FieldWrapper>

          <FieldWrapper errors={errors.email ? [errors.email] : []}>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} invalid={!!errors.email} />
          </FieldWrapper>

          <FieldWrapper errors={errors.rut ? [errors.rut] : []}>
            <Label>RUT</Label>
            <Input value={rut} onChange={handleRutChange} invalid={!!errors.rut} placeholder="12.345.678-9" />
          </FieldWrapper>

          <FieldWrapper errors={errors.phone ? [errors.phone] : []}>
            <Label>Teléfono</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} invalid={!!errors.phone} placeholder="912345678" />
          </FieldWrapper>

          <FieldWrapper>
            <Label>CV (opcional)</Label>
            <Input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={e => setCvFile(e.target.files?.[0] || null)}
            />
            {cvFile && <small className="text-muted d-block mt-1">Archivo seleccionado: {cvFile.name}</small>}
            <small className="text-muted d-block">Tamaño sugerido: máx. 2 MB</small>
          </FieldWrapper>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handleSubmit}>Crear</Button>
          <Button color="danger" onClick={toggle}>Cancelar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CreateUserModal;
